import { useState } from "react";
import DSBox from "views/system-design/DSBox";
import DSTextField from "views/system-design/DSTextField";
import DSIconButton from "views/system-design/DSIconButton";
import DSTypography from "views/system-design/DSTypography";
import DSAvatar from "views/system-design/DSAvatar";
import DSTooltip from "views/system-design/DSTooltip";
import DSPopover from "views/system-design/DSPopover";
import DSCheckbox from "views/system-design/DSCheckBox";
import DSFormControlLabel from "views/system-design/DSFormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import FilterListIcon from "@mui/icons-material/FilterList";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import ArchiveIcon from "@mui/icons-material/Archive";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { getInitials, getAvatarColor } from "../utils";
import { Author } from "api/author";
import { ViewMode } from "../types";
import { useIsMobile } from "../hooks/useIsMobile";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";

interface TicketsToolbarProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onSearchChange: (query: string) => void;
  onToggleFilters: () => void;
  showFilters: boolean;
  onCreateTicket: () => void;
  assignees: Author[];
  selectedAssignees: string[];
  onToggleAssignee: (userId: string) => void;
  showLabelPanel: boolean;
  onToggleLabelPanel: () => void;
  onlyActive: boolean;
  onToggleOnlyActive: () => void;
  onlyUnassigned: boolean;
  onToggleOnlyUnassigned: () => void;
}

export default function TicketsToolbar({
  viewMode,
  onViewModeChange,
  onSearchChange,
  onToggleFilters,
  showFilters,
  onCreateTicket,
  assignees,
  selectedAssignees,
  onToggleAssignee,
  showLabelPanel,
  onToggleLabelPanel,
  onlyActive,
  onToggleOnlyActive,
  onlyUnassigned,
  onToggleOnlyUnassigned,
}: TicketsToolbarProps) {
  const isMobile = useIsMobile();
  const maxVisibleAvatars = isMobile ? 3 : 5;
  const [assigneePopup, setAssigneePopup] = useState<HTMLElement | null>(null);
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const sortedAssignees = [...assignees].sort((first, second) => {
    const firstSelected = selectedAssignees.includes(first.id);
    const secondSelected = selectedAssignees.includes(second.id);
    if (firstSelected && !secondSelected) return -1;
    if (!firstSelected && secondSelected) return 1;
    return 0;
  });
  const visibleAssignees = sortedAssignees.slice(0, maxVisibleAvatars);
  const overflowAssignees = sortedAssignees.slice(maxVisibleAvatars);
  const overflowCount = overflowAssignees.length;

  return (
    <DSBox
      sx={{
        bgcolor: "white.5",
        borderRadius: 3,
        border: 1,
        borderColor: "surface.border",
        p: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Row 1 — Title + Search + Avatars */}
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 1 : 2,
          flexWrap: "wrap",
        }}
      >
        {!isMobile && (
          <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ConfirmationNumberIcon
              sx={{ fontSize: 28, color: "primary.main" }}
            />
            <DSTypography variant="h5" sx={{ fontWeight: "bold" }}>
              Tickets
            </DSTypography>
          </DSBox>
        )}

        <DSTextField
          placeholder="Search tickets..."
          size="small"
          onChange={(event) => onSearchChange(event.target.value)}
          sx={{
            flex: 1,
            maxWidth: isMobile ? "100%" : 360,
            minWidth: isMobile ? "100%" : 200,
            order: isMobile ? 1 : 0,
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />

        {assignees.length > 0 && (
          <DSBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {visibleAssignees.map((assignee) => {
              const displayName = assignee.name.trim();
              const isSelected = selectedAssignees.includes(assignee.id);
              return (
                <DSTooltip key={assignee.id} title={displayName}>
                  <span>
                    <DSAvatar
                      onClick={() => onToggleAssignee(assignee.id)}
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        bgcolor: getAvatarColor(displayName),
                        outline: isSelected
                          ? "2px solid"
                          : "2px solid transparent",
                        outlineColor: isSelected ? "info.main" : "transparent",
                        outlineOffset: 1,
                        opacity:
                          isSelected ||
                          (selectedAssignees.length === 0 && !onlyUnassigned)
                            ? 1
                            : 0.4,
                        transition: "opacity 0.2s, outline 0.2s",
                        "&:hover": { opacity: 1 },
                      }}
                    >
                      {getInitials(displayName)}
                    </DSAvatar>
                  </span>
                </DSTooltip>
              );
            })}
            {overflowCount > 0 && (
              <>
                <DSAvatar
                  onClick={(event) => setAssigneePopup(event.currentTarget)}
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 12,
                    fontWeight: 600,
                    bgcolor: "white.16",
                    color: "text.secondary",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "white.24" },
                  }}
                >
                  +{overflowCount}
                </DSAvatar>
                <DSPopover
                  open={!!assigneePopup}
                  anchorEl={assigneePopup}
                  onClose={() => {
                    setAssigneePopup(null);
                    setAssigneeSearch("");
                  }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  slotProps={{
                    paper: {
                      sx: {
                        minWidth: 220,
                        maxHeight: 350,
                        display: "flex",
                        flexDirection: "column",
                      },
                    },
                  }}
                >
                  <DSBox sx={{ p: 1, pb: 0.5 }}>
                    <DSTextField
                      placeholder="Search users..."
                      size="small"
                      fullWidth
                      autoFocus
                      value={assigneeSearch}
                      onChange={(event) =>
                        setAssigneeSearch(event.target.value)
                      }
                      aria-label="Search assignees"
                    />
                  </DSBox>
                  <DSBox sx={{ overflowY: "auto", p: 1, pt: 0.5 }}>
                    {(() => {
                      const filtered = sortedAssignees.filter((assignee) =>
                        assignee.name
                          .trim()
                          .toLowerCase()
                          .includes(assigneeSearch.toLowerCase()),
                      );
                      if (filtered.length === 0) {
                        return (
                          <DSTypography
                            variant="body2"
                            sx={{ color: "text.secondary", p: 1 }}
                          >
                            No matching users
                          </DSTypography>
                        );
                      }
                      return filtered.map((assignee) => {
                        const displayName = assignee.name.trim();
                        return (
                          <DSBox
                            key={assignee.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              cursor: "pointer",
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                            onClick={() => onToggleAssignee(assignee.id)}
                          >
                            <DSCheckbox
                              size="small"
                              checked={selectedAssignees.includes(assignee.id)}
                              onChange={() => onToggleAssignee(assignee.id)}
                              onClick={(event) => event.stopPropagation()}
                              sx={{ p: 0 }}
                            />
                            <DSAvatar
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: 12,
                                fontWeight: 600,
                                bgcolor: getAvatarColor(displayName),
                              }}
                            >
                              {getInitials(displayName)}
                            </DSAvatar>
                            <DSTypography variant="body2">
                              {displayName}
                            </DSTypography>
                          </DSBox>
                        );
                      });
                    })()}
                  </DSBox>
                </DSPopover>
              </>
            )}
          </DSBox>
        )}

        <DSTooltip title="Unassigned">
          <span>
            <DSAvatar
              onClick={onToggleOnlyUnassigned}
              sx={{
                width: 32,
                height: 32,
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
                bgcolor: "white.24",
                color: "text.primary",
                outline: onlyUnassigned ? "2px solid" : "2px solid transparent",
                outlineColor: onlyUnassigned ? "info.main" : "transparent",
                outlineOffset: 1,
                opacity:
                  onlyUnassigned || selectedAssignees.length === 0 ? 1 : 0.4,
                transition: "opacity 0.2s, outline 0.2s",
                "&:hover": { opacity: 1 },
              }}
            >
              ?
            </DSAvatar>
          </span>
        </DSTooltip>

        <DSBox sx={{ flex: 1 }} />

        <DSTooltip title="Archive">
          <DSIconButton
            onClick={() => onViewModeChange("archive")}
            color={viewMode === "archive" ? "primary" : "default"}
          >
            <ArchiveIcon />
          </DSIconButton>
        </DSTooltip>

        <DSTooltip title="Calendar">
          <DSIconButton
            onClick={() => onViewModeChange("calendar")}
            color={
              viewMode === "calendar" || viewMode === "weekly"
                ? "primary"
                : "default"
            }
          >
            <CalendarMonthIcon />
          </DSIconButton>
        </DSTooltip>
      </DSBox>

      {/* Row 2 — Controls */}
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          borderTop: 1,
          borderColor: "divider",
          pt: 1.5,
          mt: 1.5,
          flexWrap: "wrap",
          gap: isMobile ? 1 : 0,
        }}
      >
        <DSBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <DSBox
            sx={{
              display: "flex",
              border: 1,
              borderColor: "divider",
              borderRadius: 1.5,
              justifyContent: "center",
            }}
          >
            <DSTooltip title="Table view">
              <DSIconButton
                onClick={() => onViewModeChange("table")}
                color={viewMode === "table" ? "primary" : "default"}
                size="small"
                sx={{ flex: 1 }}
              >
                <ViewListIcon />
              </DSIconButton>
            </DSTooltip>
            <DSTooltip title="Board view">
              <DSIconButton
                onClick={() => onViewModeChange("board")}
                color={viewMode === "board" ? "primary" : "default"}
                size="small"
                sx={{ flex: 1 }}
              >
                <ViewKanbanIcon />
              </DSIconButton>
            </DSTooltip>
          </DSBox>

          {!isMobile && (
            <DSTooltip title="Toggle label panel">
              <span>
                <DSIconButton
                  onClick={onToggleLabelPanel}
                  color={showLabelPanel ? "primary" : "default"}
                >
                  <ListAltIcon />
                </DSIconButton>
              </span>
            </DSTooltip>
          )}

          <DSIconButton
            onClick={onToggleFilters}
            color={showFilters ? "primary" : "default"}
            title="Toggle filters"
          >
            <FilterListIcon />
          </DSIconButton>

          {viewMode !== "archive" && (
            <DSFormControlLabel
              control={
                <DSCheckbox
                  size="small"
                  checked={onlyActive}
                  onChange={() => onToggleOnlyActive()}
                  sx={{ p: 0.5 }}
                />
              }
              label="Only active"
              sx={{ mr: 0, "& .MuiTypography-root": { fontSize: 13 } }}
            />
          )}
        </DSBox>

        <DSBox sx={{ flex: 1 }} />

        <DSPrimaryButton
          startIcon={<AddIcon />}
          onClick={onCreateTicket}
          size={isMobile ? "small" : "medium"}
        >
          {isMobile ? "New" : "Create Ticket"}
        </DSPrimaryButton>
      </DSBox>
    </DSBox>
  );
}
